import {InfiniteData, useInfiniteQuery, UseInfiniteQueryResult} from '@tanstack/react-query';
import {donationsApi} from '../api';
import {DonationsFilter, DonationWithMetaModel} from '../model';
import {useCallback, useMemo, useState} from 'react';

type DonationsState = {
  getInfiniteDonations: UseInfiniteQueryResult<InfiniteData<DonationWithMetaModel>, Error>;
  filters: DonationsFilter;
  updateFilter: (newFilter: Partial<DonationsFilter>) => void;
};

const DEFAULT_PAGE_SIZE = 25;

export const useDonations = (appealId: string, initialFilter: DonationsFilter = {}): DonationsState => {
  const [filters, setFilters] = useState<DonationsFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteDonations = useInfiniteQuery<
    DonationWithMetaModel,
    Error,
    InfiniteData<DonationWithMetaModel>,
    ['donations', 'infinite', string, Omit<DonationsFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['donations', 'infinite', appealId, infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return donationsApi.getDonationsByAppeal(appealId, {
        ...infiniteQueryFilters,
        pageSize: filters.pageSize,
        after: pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<DonationsFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  return {
    getInfiniteDonations,
    filters,
    updateFilter,
  };
};
